/**
 * Besluit 5 (migratie 197): de werkgever bevestigt of betwist de uitvoerdatum die de opleider
 * heeft vastgelegd. Alleen een bevestigde datum komt op de Academy-verzamelfactuur van de 1e
 * (21% btw) en pas dan gaat 85% naar de opleider; een betwisting gaat terug naar de opleider.
 * Zelfde opbouw als de mijlpaal-fiat: [Bevestigen] of [Betwisten] met een verplichte reden.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { CalendarCheck, CalendarX, Receipt, Clock } from 'lucide-react';
import { bevestigUitvoerdatum, betwistUitvoerdatum } from '../../services/b2bAcademyApi';
import type { Rfq } from '../../services/b2bAcademyApi';
import { datumKort, prijs } from './b2bStijl';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

/** Vandaag als 'YYYY-MM-DD' in de tijdzone van de browser. */
const vandaagIso = () => new Date().toLocaleDateString('en-CA');

/** Welke stap de werkgever op een geaccepteerde RFQ ziet (los te testen van de UI). */
export function uitvoerdatumStap(rfq: Pick<Rfq, 'status' | 'uitvoerdatum' | 'uitvoerdatum_status' | 'gefactureerd'>, vandaag: string = vandaagIso()) {
  if (rfq.status !== 'OFFER_ACCEPTED') return null;
  if (rfq.gefactureerd) return 'gefactureerd' as const;
  if (!rfq.uitvoerdatum) return 'ingeroosterd' as const;
  if (rfq.uitvoerdatum_status === 'bevestigd') return 'bevestigd' as const;
  if (rfq.uitvoerdatum_status === 'betwist') return 'betwist' as const;
  return rfq.uitvoerdatum > vandaag ? ('ingepland' as const) : ('bevestigen' as const);
}

export const UitvoerdatumBevestiging: React.FC<{ rfq: Rfq }> = ({ rfq }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [reden, setReden] = useState<string | null>(null);
  const ververs = () => queryClient.invalidateQueries({ queryKey: ['b2b-academy'] });
  const bevestig = useMutation({ mutationFn: () => bevestigUitvoerdatum(rfq.id), onSuccess: ververs });
  const betwist = useMutation({
    mutationFn: (r: string) => betwistUitvoerdatum(rfq.id, r),
    onSuccess: () => {
      setReden(null);
      return ververs();
    },
  });
  const stap = uitvoerdatumStap(rfq);
  if (!stap) return null;

  const datum = rfq.uitvoerdatum ? datumKort(`${rfq.uitvoerdatum}T12:00:00`) : '';
  const nummer = rfq.factuur_opdracht_nummer ?? '';
  const bezig = bevestig.isPending || betwist.isPending;

  if (stap === 'ingeroosterd' || stap === 'ingepland' || stap === 'bevestigd' || stap === 'gefactureerd') {
    const tekst =
      stap === 'ingeroosterd'
        ? t('b2bAcademy.rfq.ingeroosterd', { defaultValue: 'Ingeroosterd · factuuropdracht {{nummer}} — de opleider plant de data met u in.', nummer })
        : stap === 'ingepland'
        ? t('b2bAcademy.rfq.ingeplandBevestigLater', { defaultValue: 'Ingepland t/m {{datum}} · factuuropdracht {{nummer}} — na afloop vragen wij u de uitvoerdatum te bevestigen. Pas daarna factureert BeLagom.', datum, nummer })
        : stap === 'bevestigd'
        ? t('b2bAcademy.rfq.uitvoerdatumBevestigd', { defaultValue: 'Uitvoerdatum {{datum}} bevestigd · factuuropdracht {{nummer}} — komt op de Academy-verzamelfactuur van de 1e van de maand.', datum, nummer })
        : t('b2bAcademy.rfq.gefactureerd', { defaultValue: 'Gefactureerd op de Academy-verzamelfactuur · training {{datum}} · factuuropdracht {{nummer}}.', datum, nummer });
    const Icoon = stap === 'ingepland' ? Clock : Receipt;
    return (
      <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-700">
        <Icoon className="h-4 w-4 shrink-0" aria-hidden="true" />
        {tekst}
      </p>
    );
  }

  if (stap === 'betwist') {
    return (
      <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        <p className="inline-flex items-center gap-1.5 font-medium text-gray-900">
          <CalendarX className="h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
          {t('b2bAcademy.rfq.uitvoerdatumBetwist', { defaultValue: 'U heeft de uitvoerdatum {{datum}} betwist', datum })}
        </p>
        {rfq.uitvoerdatum_betwist_reden && <p className="mt-1 whitespace-pre-line text-xs text-gray-600">“{rfq.uitvoerdatum_betwist_reden}”</p>}
        <p className="mt-1 text-xs text-gray-500">{t('b2bAcademy.rfq.uitvoerdatumBetwistUitleg', { defaultValue: 'De opleider legt een nieuwe datum vast; u krijgt dan opnieuw een melding om te bevestigen. Tot die tijd wordt er niet gefactureerd.' })}</p>
      </div>
    );
  }

  // stap === 'bevestigen'
  return (
    <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900">
        <CalendarCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('b2bAcademy.rfq.bevestigTitel', { defaultValue: 'Bevestig de uitvoerdatum: {{datum}}', datum })}
      </p>
      <p className="mt-1 text-xs text-amber-900/80">
        {t('b2bAcademy.rfq.bevestigUitleg', {
          defaultValue: 'De opleider meldt dat de training t/m {{datum}} is uitgevoerd. Pas na uw bevestiging komt {{bedrag}} (excl. 21% btw) op de Academy-verzamelfactuur van de 1e van de maand. Klopt de datum niet, betwist hem dan.',
          datum,
          bedrag: prijs(rfq.offertewaarde ?? 0),
        })}
      </p>

      {(bevestig.isError || betwist.isError) && (
        <p className="mt-2 text-sm text-red-600" role="alert">{foutMelding(bevestig.error ?? betwist.error, t('b2bAcademy.rfq.fout', { defaultValue: 'De actie is niet gelukt. Probeer het opnieuw.' }))}</p>
      )}

      {reden === null ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => bevestig.mutate()} disabled={bezig} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:brightness-110 disabled:opacity-60">
            <CalendarCheck className="h-4 w-4" aria-hidden="true" />
            {t('b2bAcademy.rfq.bevestig', { defaultValue: 'Datum bevestigen' })}
          </button>
          <button type="button" onClick={() => setReden('')} disabled={bezig} className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-900 disabled:opacity-60">
            {t('b2bAcademy.rfq.betwist', { defaultValue: 'Datum betwisten' })}
          </button>
        </div>
      ) : (
        <div className="mt-3 border-t border-amber-200 pt-3">
          <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor={`betwist-${rfq.id}`}>
            {t('b2bAcademy.rfq.betwistReden', { defaultValue: 'Waarom klopt de uitvoerdatum niet?' })}
          </label>
          <textarea
            id={`betwist-${rfq.id}`}
            className="w-full rounded-md border border-gray-300 p-2 text-sm"
            rows={2}
            maxLength={1000}
            value={reden}
            onChange={(e) => setReden(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => betwist.mutate(reden.trim())} disabled={bezig || reden.trim().length < 3} className="rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:brightness-110 disabled:opacity-60">
              {t('b2bAcademy.rfq.betwistVerstuur', { defaultValue: 'Betwisting versturen' })}
            </button>
            <button type="button" onClick={() => setReden(null)} disabled={bezig} className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:border-gray-400 disabled:opacity-60">
              {t('b2bAcademy.rfq.annuleer', { defaultValue: 'Annuleren' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UitvoerdatumBevestiging;
